import React, { createContext, useContext, useRef, useState, ReactNode } from "react";
import NotificationContainer from "../components/notification_balloons/NotificationContainer";

type NotificationItem = {
  id: number;
  content: ReactNode;
  backgroundColor?: string;
};

type NotifyOptions = {
  autoCloseMs?: number;
  backgroundColor?: string;
};

type NotificationContextValue = {
  notify: (content: ReactNode, opts?: NotifyOptions) => void;
  close: (id: number) => void;
};

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export default function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const idRef = useRef(0);

  const close = (id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const notify = (content: ReactNode, opts?: NotifyOptions) => {
    const id = ++idRef.current;
    const newNotification: NotificationItem = {
      id,
      content,
      backgroundColor: opts?.backgroundColor,
    };
    setNotifications((prev) => [...prev, newNotification]);

    const ms = opts?.autoCloseMs ?? 3000;
    if (ms > 0) {
      setTimeout(() => close(id), ms);
    }
  };

  return (
    <NotificationContext.Provider value={{ notify, close }}>
      {children}
      <div className="absolute right-2 bottom-2 flex flex-col gap-2">
        {notifications.map((n) => (
          <NotificationContainer
            key={n.id}
            backgroundColor={n.backgroundColor}
            onClose={() => close(n.id)}
          >
            {n.content}
          </NotificationContainer>
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used within <NotificationProvider>");
  return ctx;
}